import { createClient, type SupabaseClient } from '@supabase/supabase-js';
import { AppError, type ErrorCode } from './errors.ts';
import type { Booking, BookingStatus } from '../utils/validation.ts';

/**
 * Shared Supabase plumbing for both apps. Production: the EU (Frankfurt)
 * project. Local: the gateway of `npm run dev` (scripts/local/gateway.ts).
 */

let client: SupabaseClient | null = null;

/** True when the page was opened from a password-reset or invitation link (read before supabase-js consumes the fragment). */
export const openedFromAuthLink = typeof location !== 'undefined' && /type=(recovery|invite)/.test(location.hash);

export function supabase(): SupabaseClient {
  if (!client) {
    const url = import.meta.env.VITE_SUPABASE_URL;
    const key = import.meta.env.VITE_SUPABASE_ANON_KEY;
    if (!url || !key) throw new AppError('generic', 'VITE_SUPABASE_URL / VITE_SUPABASE_ANON_KEY missing');
    client = createClient(url, key, { auth: { persistSession: true, detectSessionInUrl: true, flowType: 'implicit' } });
  }
  return client;
}

/** Realtime is off for the local gateway; the apps fall back to polling. */
export const realtimeEnabled = import.meta.env.VITE_SUPABASE_REALTIME !== 'false';

// --- Errors ----------------------------------------------------------------------

/** Exceptions raised in SQL carry stable codes (see db/migrations/0002, 0003). */
const SQL_ERRORS: Record<string, ErrorCode> = {
  slot_taken: 'slot_taken',
  day_full: 'slot_taken',
  rate_limited: 'rate_limited',
  outside_window: 'outside_window',
  outside_hours: 'outside_window',
  closed: 'outside_window',
  too_late: 'outside_window',
  not_found: 'not_found',
  tenant_not_found: 'not_found',
  invalid_service: 'invalid',
  invalid_barber: 'invalid',
  invalid_input: 'invalid',
};

export function toAppError(err: { message?: string; code?: string } | null | undefined): AppError {
  const message = err?.message ?? '';
  const code = err?.code ?? '';
  if (SQL_ERRORS[message]) return new AppError(SQL_ERRORS[message], message);
  if (code === '23P01') return new AppError('slot_taken', message); // exclusion_violation
  if (code === '42501' || code === 'PGRST301' || /JWT|permission denied/i.test(message)) return new AppError('unauthorized', message);
  if (/fetch|network|Failed to fetch|Load failed/i.test(message)) return new AppError('network', message);
  return new AppError('generic', message || code);
}

export function unwrap<T>(res: { data: T | null; error: { message: string; code?: string } | null }): T {
  if (res.error) throw toAppError(res.error);
  if (res.data === null) throw new AppError('not_found');
  return res.data;
}

export function unwrapVoid(res: { error: { message: string; code?: string } | null }): void {
  if (res.error) throw toAppError(res.error);
}

// --- Rows ------------------------------------------------------------------------

export type BookingRow = {
  id: string;
  service_id: string;
  barber_id: string;
  starts_at: string;
  ends_at: string;
  duration_min: number;
  price_cents: number;
  status: BookingStatus;
  client_name: string;
  client_phone?: string | null;
  client_email?: string | null;
  cancel_token?: string | null;
  created_at: string;
  updated_at?: string;
  locale?: string | null;
};

export const iso = (v: string) => new Date(v).toISOString();

export function mapRow(r: BookingRow, slug: string, cancelToken?: string): Booking {
  return {
    id: r.id,
    tenant_slug: slug,
    service_id: r.service_id,
    barber_id: r.barber_id,
    starts_at: iso(r.starts_at),
    ends_at: iso(r.ends_at),
    duration_min: r.duration_min,
    price: r.price_cents / 100,
    status: r.status,
    client_name: r.client_name,
    client_phone: r.client_phone ?? '',
    client_email: r.client_email ?? undefined,
    cancel_token: cancelToken ?? r.cancel_token ?? '',
    created_at: iso(r.created_at),
    locale: r.locale === 'en' ? 'en' : 'de',
  };
}
